import { ColumnsProps } from '@/Helper';
import cx from 'classnames';
import React from 'react';

import { EventPaginationSetting, EventSortDirection } from '@/model/AlarmModel';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import styles from './virtualTable.module.scss';

type Props<T> = {
	column: ColumnsProps<T>;
	separator: boolean;
	onSort: (paginationSettings: EventPaginationSetting) => void;
	paginationSettings: EventPaginationSetting;
	style?: React.CSSProperties;
};

const NonActionHeaderColumn = <T,>({ column, separator, onSort, paginationSettings, style }: Props<T>): JSX.Element => {
	const handleOnSort = (field: string) => {
		const newPaginationSettings: EventPaginationSetting = {
			...paginationSettings,
			PageNumber: 1,
			SortField: field,
		};

		if (paginationSettings.SortField !== field) {
			newPaginationSettings.SortDirection = EventSortDirection.Ascending;
		} else {
			switch (paginationSettings.SortDirection) {
				case EventSortDirection.Ascending:
					newPaginationSettings.SortDirection = EventSortDirection.Descending;
					break;
				case EventSortDirection.Descending:
					newPaginationSettings.SortDirection = EventSortDirection.OperatorPreference;
					newPaginationSettings.SortField = '';
					break;
			}
		}
		onSort(newPaginationSettings);
	};

	const getContentColumn = (): JSX.Element => {
		let columnContent: JSX.Element = column.title ? <span>{column.title as React.ReactNode}</span> : null;
		if (column.sorter) {
			let tooltipTitle = _('ClickToSortAscending');
			if (paginationSettings.SortField === String(column.dataIndex)) {
				switch (paginationSettings.SortDirection) {
					case EventSortDirection.Ascending:
						tooltipTitle = _('ClickToSortDescending');
						break;
					case EventSortDirection.Descending:
						tooltipTitle = _('ClickToCancelSorting');
						break;
				}
			}
			columnContent = (
				<Tooltip title={tooltipTitle}>
					<div
						className={cx({ [styles.sortableColumn]: column.sorter }, styles.columContent)}
						onClick={() => {
							if (column.sorter) {
								handleOnSort(String(column.dataIndex));
							}
						}}
					>
						{columnContent}
						<span className={styles.sortIcons}>
							<CaretUpOutlined
								className={cx({
									[styles.sortActive]:
										paginationSettings.SortDirection === EventSortDirection.Ascending && paginationSettings.SortField === column.dataIndex,
								})}
							/>
							<CaretDownOutlined
								className={cx({
									[styles.sortActive]:
										paginationSettings.SortDirection === EventSortDirection.Descending && paginationSettings.SortField === column.dataIndex,
								})}
							/>
						</span>
					</div>
				</Tooltip>
			);
		}
		return columnContent;
	};

	return (
		<div
			key={`header-${String(column.dataIndex)}`}
			className={cx(styles.tableHeaderColumn, { [styles.separator]: separator })}
			style={{ ...style, justifyContent: column.align }}
		>
			{getContentColumn()}
		</div>
	);
};

export { NonActionHeaderColumn };
